export function getThreadDisplaySubject(subject = 'Unknown') {
  const stripped = String(subject || 'Unknown')
    .replace(/^\s*((re|fw|fwd):\s*)+/i, '')
    .trim();

  const tourSlotMatch = stripped.match(/^tour\s+slot:\s*(.+?)(?:\s+on\s+(?:(?:monday|tuesday|wednesday|thursday|friday|saturday|sunday),?\s+)?[a-z]+\s+\d{1,2},\s+\d{4})?\s+at\s+(?:\d{1,2}(?::\d{2})?\s*(?:am|pm)|noon|morning|afternoon)\s*$/i);
  if (tourSlotMatch?.[1]?.trim()) {
    return tourSlotMatch[1].trim();
  }

  return stripped || 'Unknown';
}

// A campaign subject is "<address>[, <suite / property name>][, <city>]" -
// buildCampaignPropertySubject (campaignSubject.js) carries the suite because
// two rows of one campaign can sit at the same street address and differ only
// by it. So the property key has to carry the suite too: keying on the address
// before the first comma threw 1200 Market Street Suite 210 and Suite 340 into
// one group, and the dashboard then rendered one of them and hid the other
// along with its status, its Stop control and its messages (DEF-4).
//
// The identity is everything except the trailing locality, which is how the
// backend reads a row anchor (parseRowAnchor in campaignResults.js). The one
// exception is a subject whose trailing segment IS the unit designator, which
// happens when the upload carried no city - dropping it there would recreate
// the collision.
const UNIT_DESIGNATOR = /^(?:\d+|#\s*\S+|(?:suite|ste|unit|apt|apartment|floor|fl|bldg|building|room|rm|lot|space|spc|dock|door)\b.*)$/i;

function isUnitDesignatorSegment(segment) {
  return UNIT_DESIGNATOR.test(String(segment || '').trim());
}

export function getThreadPropertyKey(subject = 'Unknown') {
  const displaySubject = getThreadDisplaySubject(subject);
  const segments = displaySubject.split(',').map(part => part.trim()).filter(Boolean);

  if (segments.length < 2) return displaySubject;

  const identity = isUnitDesignatorSegment(segments[segments.length - 1])
    ? segments
    : segments.slice(0, -1);

  return identity.join(', ');
}

export function threadSubjectMatchesRowAnchor(subject, rowAnchor, threads = [{ subject }]) {
  if (!subject || !rowAnchor) return false;
  const subjectKey = getThreadPropertyKey(subject).toLowerCase();
  const anchorKey = getThreadPropertyKey(rowAnchor).toLowerCase();

  const matchesKey = key => key === anchorKey || (
    !anchorKey.includes(',') && key.split(',')[0].trim() === anchorKey
  );

  return matchesKey(subjectKey) && threads.filter(thread => (
    thread.subject && matchesKey(getThreadPropertyKey(thread.subject).toLowerCase())
  )).length === 1;
}

export function isAmbiguousStreetAnchor(rowAnchor, subjects = []) {
  if (!rowAnchor) return false;
  const anchorKey = getThreadPropertyKey(rowAnchor).toLowerCase();
  if (anchorKey.includes(',')) return false;
  return subjects.filter(subject => subject && (
    getThreadPropertyKey(subject).toLowerCase().split(',')[0].trim() === anchorKey
  )).length > 1;
}
